import * as React from "react";
import { useDemoAccess } from "@/hooks/use-demo-access";
import { hasNavigationAccess, NavigationItemLink, NavigationSidebarItemsList } from "./Sidebar.const";
import { Link, useLocation } from "react-router";
import { SidebarSeparator } from "@/components/sidebar/SidebarSeparator";
import { Tooltip } from "@/components/ui/tooltip";
import { isNavigationPathActive } from "./navigationPath";

function classNames(...classes: (string | undefined | null)[]): string {
  return classes.filter(Boolean).join(" ");
}

export const NavigationMenuList: React.FC = () => {
  const { pathname } = useLocation();
  const { hasFeature } = useDemoAccess();

  const filteredNavigation = NavigationSidebarItemsList.filter(
    item => item.type === "separator" || hasNavigationAccess(item, hasFeature),
  );

  const updatedNavigation = filteredNavigation.map(item => ({
    ...item,
    current:
      item.type === "item" &&
      item.href !== undefined &&
      (isNavigationPathActive(pathname, item.href) || (item.href === "/drive" && pathname === "/shared")),
    children:
      item.type === "item"
        ? item.children
            ?.filter(subItem => subItem.type === "separator" || hasNavigationAccess(subItem, hasFeature))
            .map(subItem => ({
              ...subItem,
              current:
                subItem.type === "item" && subItem.href !== undefined && isNavigationPathActive(pathname, subItem.href),
            }))
        : [],
  }));

  const renderLink = (item: NavigationItemLink) => (
    <Link
      to={item.href || ""}
      aria-current={item.current ? "page" : undefined}
      className={classNames(
        item.current ? "bg-ui-sidebar-text text-white" : "text-atriablue-500 hover:bg-white hover:text-gray-600",
        "flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors duration-200 ease-in-out",
      )}
    >
      {item.icon && <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />}
      <span className="flex-1 text-sm">{item.name}</span>
    </Link>
  );

  return (
    <nav className="mt-8 rounded-lg text-sm text-ui-sidebar-text">
      {updatedNavigation.map((item, index) => {
        if (item.type === "separator") {
          return <SidebarSeparator key={index} />;
        }

        return (
          <div key={item.name} className="mb-1">
            {item.href ? (
              item.tooltip ? (
                <Tooltip text={item.tooltip} className="rounded bg-black text-white" side="right">
                  {renderLink(item)}
                </Tooltip>
              ) : (
                renderLink(item)
              )
            ) : (
              <span
                className={classNames(
                  item.current ? "text-white" : "text-white",
                  "flex items-center gap-2 px-2 py-1.5",
                )}
              >
                {item.icon && <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />}
                <span className="flex-1 text-sm">{item.name}</span>
              </span>
            )}
            {item.children && (
              <div className="pl-3">
                {item.children.map((subItem, index) => {
                  if (subItem.type === "separator") {
                    return <SidebarSeparator key={index} />;
                  }
                  return subItem.href ? (
                    <Link
                      key={subItem.name}
                      to={subItem.href}
                      aria-current={subItem.current ? "page" : undefined}
                      className={classNames(
                        subItem.current ? "text-white" : "text-gray-400",
                        "flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors duration-200 ease-in-out hover:bg-gray-800 hover:text-white",
                      )}
                    >
                      {subItem.icon && <subItem.icon className="h-4 w-4 shrink-0" aria-hidden="true" />}
                      <span className="flex-1 text-xs">{subItem.name}</span>
                    </Link>
                  ) : (
                    <span
                      key={subItem.name}
                      className={classNames(
                        subItem.current ? "text-white" : "text-gray-400",
                        "flex items-center gap-2 px-2 py-1.5",
                      )}
                    >
                      {subItem.icon && <subItem.icon className="h-4 w-4 shrink-0" aria-hidden="true" />}
                      <span className="flex-1 text-xs">{subItem.name}</span>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};
